import React from "react";

import type { CreateMeetingRequestJson, ExtendedWorkspace } from "@performyard/service-contracts";

import { CalendarErrorModal } from "./CalendarErrorModal";
import MeetingForm, { type MeetingFormData } from "./MeetingForm";
import { VideoConferencingErrorModal } from "./VideoConferencingErrorModal";

import { useAddWorkspaceMeeting } from "../../../../../api/hooks";
import { createErrorModalResubmit, useCalendarErrorHandler, useVideoConferencingErrorHandler } from "./hooks";

import { meetingFormSchema } from "./meeting-form-schema";
import { getDefaultFormValues } from "./meeting-form-utils";
import { errorModalActions, useMeetingFormState, useRunOnOpen } from "./use-meeting-form-modal";

type CreateMeetingModalProps = {
	isOpen: boolean;
	handleClose: () => void;
	workspace: ExtendedWorkspace;
	workspaceHasMeetings: boolean;
	setExpandedMeetingIds?: React.Dispatch<React.SetStateAction<string[]>>;
};

type CreateMeetingVariables = { workspaceId: string; data: CreateMeetingRequestJson };

export const CreateMeetingModal = ({
	isOpen,
	handleClose,
	workspace,
	workspaceHasMeetings,
	setExpandedMeetingIds = () => {},
}: CreateMeetingModalProps): React.ReactElement => {
	const { modalState: calendarModalState, handleCalendarError, closeModal, setRetrying } = useCalendarErrorHandler();
	const {
		modalState: vcModalState,
		handleVideoConferencingError,
		closeModal: closeVcModal,
		setRetrying: setVcRetrying,
	} = useVideoConferencingErrorHandler();

	const { mutate: addWorkspaceMeeting, isPending } = useAddWorkspaceMeeting(
		handleClose,
		setExpandedMeetingIds,
		(error, variables) => {
			const context = {
				operation: "create" as const,
				meetingData: variables.data,
				workspaceId: variables.workspaceId,
			};
			// Conferencing first, so its own modal shows before the calendar one.
			if (handleVideoConferencingError(error, context)) return true;
			return handleCalendarError(error, context);
		}
	);
	const resubmitAfterVcError = createErrorModalResubmit(addWorkspaceMeeting, setVcRetrying, closeVcModal);
	const resubmitAfterCalendarError = createErrorModalResubmit(addWorkspaceMeeting, setRetrying, closeModal);

	const { formValues, validationErrors, updateForm, setNewDefaultValues, validateForm, resetValidationErrors } =
		useMeetingFormState(meetingFormSchema);

	useRunOnOpen(isOpen, () => {
		setNewDefaultValues({ ...getDefaultFormValues(), workspaceId: workspace._id });
		resetValidationErrors();
	});

	const handleSubmit = (data: MeetingFormData): void => {
		if (!validateForm()) {
			return;
		}

		const meetingParticipants = workspace.participants?.map((participant) => ({
			userId: participant.userId,
			role: participant.role,
		}));

		const formattedData: CreateMeetingRequestJson = {
			...data,
			startDateTime: data.startDateTime.toISOString(),
			endDateTime: data.endDateTime.toISOString(),
			participants: meetingParticipants,
			// The form holds the chosen provider (null = none); omit it from the request when none.
			videoConferencingProvider: data.videoConferencingProvider ?? undefined,
		};

		addWorkspaceMeeting({ workspaceId: workspace._id, data: formattedData });
	};

	const onClose = (): void => {
		if (calendarModalState.type !== "none") {
			closeModal();
			return;
		}
		if (vcModalState.type !== "none") {
			closeVcModal();
			return;
		}

		handleClose();
		resetValidationErrors();
	};

	const failedVariables = (
		state: typeof vcModalState | typeof calendarModalState
	): CreateMeetingVariables | undefined =>
		state.type === "error" && state.context.operation === "create"
			? { workspaceId: state.context.workspaceId, data: state.context.meetingData }
			: undefined;

	return (
		<>
			{calendarModalState.type === "none" && vcModalState.type === "none" && (
				<MeetingForm
					isOpen={isOpen}
					onClose={onClose}
					onSubmit={handleSubmit}
					formValues={formValues}
					onFormChange={updateForm}
					validationErrors={validationErrors}
					submitLabel="Create Meeting"
					isPending={isPending}
					title="Create a New Meeting"
					showCarryOver={workspaceHasMeetings}
					showMeetingIntegrations={true}
				/>
			)}

			<VideoConferencingErrorModal
				modalState={vcModalState}
				{...errorModalActions(failedVariables(vcModalState), resubmitAfterVcError, (variables) => ({
					...variables,
					data: { ...variables.data, videoConferencingProvider: undefined },
				}))}
				onClose={closeVcModal}
			/>

			<CalendarErrorModal
				modalState={calendarModalState}
				{...errorModalActions(failedVariables(calendarModalState), resubmitAfterCalendarError, (variables) => ({
					...variables,
					data: { ...variables.data, addToCalendar: false },
				}))}
				onClose={closeModal}
			/>
		</>
	);
};
